import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/**
 * The primary interactive control. Rounded, tactile, and theme-aware — all
 * colors come from CSS variables so it adapts to any theme automatically.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={cn(
        'as-btn',
        `as-btn--${variant}`,
        `as-btn--${size}`,
        fullWidth && 'as-btn--block',
        loading && 'as-btn--loading',
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 22 : 18} />
      ) : (
        <>
          {leftIcon && <span className="as-btn__icon">{leftIcon}</span>}
          {children && <span className="as-btn__label">{children}</span>}
          {rightIcon && <span className="as-btn__icon">{rightIcon}</span>}
        </>
      )}
    </button>
  );
}
